import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hero3D } from '../components/Hero3D';
import { GameCard } from '../components/GameCard';
import { api } from '../services/api';
import { Game } from '../types';
import {
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Zap,
  Globe2,
  Gamepad2,
  Layers,
  Award,
} from 'lucide-react';

export const HomePage: React.FC = () => {
  const [featuredGames, setFeaturedGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const games = await api.getGames();
        setFeaturedGames(games.slice(0, 3));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  return (
    <div className="space-y-28 pb-20">
      {/* HERO SECTION WITH INTERACTIVE 3D */}
      <section className="relative overflow-hidden pt-12 md:pt-20">
        {/* Ambient Glows */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Copy */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left z-10">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-950/60 border border-cyan-500/30 text-cyan-300 text-xs font-semibold backdrop-blur-md">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Next-Gen Gaming Studio</span>
              </div>

              <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-[1.1]">
                Forging Immersive{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-300 to-fuchsia-400">
                  Virtual Realities
                </span>{' '}
                & Next-Gen Worlds.
              </h1>

              <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                Welcome to <strong className="text-white">GSR STUDIO</strong>. We merge cutting-edge real-time physics, high-fidelity graphics, and cloud-native architectures to deliver groundbreaking multiplayer and narrative experiences.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-4">
                <Link
                  to="/games"
                  className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 font-bold text-sm flex items-center justify-center gap-2 shadow-xl shadow-cyan-500/20 transition-all hover:scale-[1.02]"
                >
                  <Gamepad2 className="w-4 h-4 fill-current" />
                  <span>Discover Games</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>

                <Link
                  to="/contact"
                  className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-slate-900/80 hover:bg-slate-800 text-white font-semibold text-sm flex items-center justify-center gap-2 border border-slate-700/80 transition-all"
                >
                  <span>Get in Touch</span>
                </Link>
              </div>

              {/* Quick Specs / Trust badges */}
              <div className="pt-8 grid grid-cols-3 gap-4 border-t border-slate-800/80 max-w-lg mx-auto lg:mx-0">
                <div>
                  <p className="text-2xl font-extrabold text-white">4+</p>
                  <p className="text-xs text-slate-400">Flagship Titles</p>
                </div>
                <div>
                  <p className="text-2xl font-extrabold text-cyan-400">120 FPS</p>
                  <p className="text-xs text-slate-400">Optimized Target</p>
                </div>
                <div>
                  <p className="text-2xl font-extrabold text-purple-400">Edge</p>
                  <p className="text-xs text-slate-400">Cloud Powered</p>
                </div>
              </div>
            </div>

            {/* Right: 3D Canvas */}
            <div className="lg:col-span-5 relative flex items-center justify-center">
              <div className="w-full max-w-[480px] aspect-square rounded-3xl bg-slate-900/30 border border-slate-800/80 p-2 backdrop-blur-xl relative shadow-2xl shadow-cyan-500/5 group">
                <Hero3D />

                {/* Interactive Hint */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-950/80 border border-slate-800 px-3.5 py-1.5 rounded-full backdrop-blur-md pointer-events-none flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-[11px] text-slate-400 font-medium">
                    Move cursor to rotate 3D core
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS / TECH SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
          <h2 className="text-xs uppercase tracking-widest text-cyan-400 font-bold">
            Pioneering Game Engineering
          </h2>
          <p className="text-3xl font-extrabold text-white tracking-tight">
            Built from the ground up for zero compromise
          </p>
          <p className="text-slate-400 text-sm">
            We don't settle for standard templates. GSR Studio engineers every frame, shader, and packet for peak competitive feel.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-slate-900/40 rounded-2xl border border-slate-800 p-8 space-y-4 hover:border-cyan-500/40 transition">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
              <Zap className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-white">Sub-Millisecond Edge Sync</h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              Leveraging Cloudflare global edge datacenters for peer matching and game state relay, minimizing latency and eliminating tick rate jitter.
            </p>
          </div>

          <div className="bg-slate-900/40 rounded-2xl border border-slate-800 p-8 space-y-4 hover:border-purple-500/40 transition">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
              <Layers className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-white">Advanced Real-Time Shaders</h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              Custom volumetric illumination, ray-traced reflections, and micro-polygon geometry pipelines that push modern graphics hardware to the limit.
            </p>
          </div>

          <div className="bg-slate-900/40 rounded-2xl border border-slate-800 p-8 space-y-4 hover:border-emerald-500/40 transition">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-white">Player-First Philosophy</h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              No predatory mechanics. Transparent roadmap updates, active community playtesting, and direct feedback loops with our development teams.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED GAMES SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-end justify-between mb-12 gap-4">
          <div>
            <h2 className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-2">
              Featured Works
            </h2>
            <h3 className="text-3xl font-extrabold text-white tracking-tight">
              Our Flagship Titles
            </h3>
          </div>
          <Link
            to="/games"
            className="text-sm font-semibold text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5 transition"
          >
            <span>View All Games</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 rounded-2xl bg-slate-900/50 animate-pulse border border-slate-800" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        )}
      </section>

      {/* COMMUNITY & CALL TO ACTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-gradient-to-r from-cyan-950/40 via-slate-900 to-purple-950/40 border border-slate-800 p-8 sm:p-14 relative overflow-hidden text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-4 max-w-xl z-10">
            <h3 className="text-3xl font-extrabold text-white tracking-tight">
              Ready to Shape the Future of Gaming with Us?
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              Whether you are an aspiring game tester, a publisher looking for partnership, or a talent ready to build worlds, GSR STUDIO welcomes you.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 z-10">
            <Link
              to="/contact"
              className="px-6 py-3.5 rounded-xl bg-white text-slate-950 font-bold text-sm hover:bg-slate-200 transition shadow-lg"
            >
              Contact GSR Studio
            </Link>
            <Link
              to="/staff"
              className="px-6 py-3.5 rounded-xl bg-slate-800/80 text-white font-semibold text-sm hover:bg-slate-700 border border-slate-700 transition"
            >
              Meet the Creators
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
